
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useWedding } from './WeddingContext';
import { getAuthenticatedSupabase } from '../supabaseClient';
import { AiConfig, ChatMessage, DEFAULT_MODELS, AiProviderId } from '../lib/ai/types';
import { getApiKey, getBaseUrl, getChatHistory, saveChatHistory, clearChatHistory } from '../lib/ai/storage';
import { sendChat } from '../lib/ai/client';
import { buildSystemPrompt } from '../lib/ai/prompts';

interface AiContextType {
  isOpen: boolean;
  toggleDrawer: () => void;
  openCopilot: () => void;
  closeCopilot: () => void;
  messages: ChatMessage[];
  config: AiConfig;
  isLoading: boolean;
  error: string | null;
  isDemoMode: boolean;
  sendMessage: (content: string) => Promise<void>;
  updateConfig: (updates: Partial<AiConfig>) => void;
  clearHistory: () => void;
}

const AiContext = createContext<AiContextType | undefined>(undefined);

// Demo response generator
const getDemoResponse = (input: string): string => {
  const lower = input.toLowerCase();
  if (lower.includes('úkol') || lower.includes('ukol')) {
    return "(DEMO) Zde je návrh úkolů:\n1. Rezervovat termín\n2. Vybrat fotografa\n3. Sestavit seznam hostů\n\nPro reálné návrhy prosím vložte API klíč.";
  }
  if (lower.includes('harmonogram')) {
    return "(DEMO) Příklad harmonogramu:\n13:00 Obřad\n13:45 Focení\n14:30 Oběd\n\nPro generování na míru prosím vložte API klíč.";
  }
  if (lower.includes('rytina') || lower.includes('prst')) {
      return "(DEMO) Nápady na rytinu:\n- Navždy spolu\n- 20.6.2025\n- Miluji Tě\n- Iniciály A&P\n\nPro více nápadů vložte API klíč.";
  }
  return "(DEMO) Jsem v ukázkovém režimu. Mohu vám pomoci s plánováním, ale pro plnou funkčnost a chytré odpovědi je potřeba vložit API klíč v nastavení (OpenAI, Anthropic nebo Gemini).";
};

export const AiContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { activeWedding } = useWedding();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [config, setConfig] = useState<AiConfig>({
    provider: 'openai',
    model: DEFAULT_MODELS['openai'][0].id,
    includeContext: true,
    includeSensitive: false
  });

  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    // Debug mount
    console.log('🤖 AI Context Provider Mounted');
  }, []);

  // Load history when wedding changes
  useEffect(() => {
    if (activeWedding) {
      setMessages(getChatHistory(activeWedding.id));
    } else {
      setMessages([]);
    }
  }, [activeWedding]);

  const toggleDrawer = () => setIsOpen(prev => !prev);
  const openCopilot = () => setIsOpen(true);
  const closeCopilot = () => setIsOpen(false);

  const updateConfig = (updates: Partial<AiConfig>) => {
    setConfig(prev => ({ ...prev, ...updates }));
  };

  const clearHistory = () => {
    if (activeWedding) {
      clearChatHistory(activeWedding.id);
      setMessages([]);
    }
  };

  const fetchContextData = async () => {
    if (!activeWedding) return null;
    const supabase = getAuthenticatedSupabase();
    const [guests, tasks, vendors, budget, schedule, rings, cMeta, cSteps] = await Promise.all([
      supabase.from('guests').select('*').eq('wedding_id', activeWedding.id),
      supabase.from('tasks').select('*').eq('wedding_id', activeWedding.id),
      supabase.from('vendors').select('*').eq('wedding_id', activeWedding.id),
      supabase.from('budget_items').select('*').eq('wedding_id', activeWedding.id),
      supabase.from('schedule_items').select('*').eq('wedding_id', activeWedding.id).order('start_time', { ascending: true }),
      supabase.from('ring_items').select('*').eq('wedding_id', activeWedding.id),
      supabase.from('ceremony_meta').select('*').eq('wedding_id', activeWedding.id).single(),
      supabase.from('ceremony_steps').select('*').eq('wedding_id', activeWedding.id).order('order_index', { ascending: true })
    ]);

    return {
      wedding: activeWedding,
      guests: guests.data || [],
      tasks: tasks.data || [],
      vendors: vendors.data || [],
      budget: budget.data || [],
      schedule: schedule.data || [],
      rings: rings.data || [],
      ceremonyMeta: cMeta.data || null,
      ceremonySteps: cSteps.data || [],
      includeSensitive: config.includeSensitive
    };
  };

  const sendMessage = async (content: string) => {
    if (!activeWedding && !isDemoMode) return;
    
    const apiKey = getApiKey(config.provider);
    
    // DEMO MODE CHECK
    const isDemo = !apiKey;
    setIsDemoMode(isDemo);
    
    setError(null);

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: Date.now()
    };

    const newHistory = [...messages, userMsg];
    setMessages(newHistory);
    if (activeWedding) saveChatHistory(activeWedding.id, newHistory);
    setIsLoading(true);

    try {
      let responseText = '';

      if (isDemo) {
        // Mock delay
        await new Promise(r => setTimeout(r, 1000));
        responseText = getDemoResponse(content);
      } else {
        let systemPrompt = '';
        if (config.includeContext) {
          const contextData = await fetchContextData();
          systemPrompt = buildSystemPrompt(contextData);
        } else {
          systemPrompt = buildSystemPrompt(null);
        }

        responseText = await sendChat({
          provider: config.provider,
          model: config.model,
          apiKey,
          baseUrl: config.provider === 'custom' ? getBaseUrl() : undefined,
          messages: newHistory,
          systemPrompt
        });
      }

      const aiMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: responseText,
        timestamp: Date.now()
      };

      const finalHistory = [...newHistory, aiMsg];
      setMessages(finalHistory);
      if (activeWedding) saveChatHistory(activeWedding.id, finalHistory);

    } catch (err: any) {
      console.error(err);
      setError(err.message || 'Error generating response');
      const errorMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'system',
        content: `Error: ${err.message || 'Unknown error occurred.'}`,
        timestamp: Date.now()
      };
      setMessages([...newHistory, errorMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AiContext.Provider value={{
      isOpen,
      toggleDrawer,
      openCopilot,
      closeCopilot,
      messages,
      config,
      isLoading,
      error,
      isDemoMode,
      sendMessage,
      updateConfig,
      clearHistory
    }}>
      {children}
    </AiContext.Provider>
  );
};

export const useAi = () => {
  const context = useContext(AiContext);
  if (!context) throw new Error('useAi must be used within AiContextProvider');
  return context;
};
